import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@txnlab/use-wallet';
import algosdk from 'algosdk';
import Button from '@/components/Button';
import useSKClubAuth from '@/hooks/useSKClubAuth';
import { useBalanceStore } from '@/hooks/useBalanceStore';
import { algodClient } from '@/utils/algo';
import config from '@/config';
import toast from 'react-hot-toast';

export default function SKClubExchange() {
  const { getIdToken, coinBalance } = useSKClubAuth();
  const { activeAddress, signTransactions } = useWallet();
  const { walletBalance } = useBalanceStore();
  const [direction, setDirection] = useState('treats_to_coins');
  const [amount, setAmount] = useState('');
  const [exchanging, setExchanging] = useState(false);
  const [allowance, setAllowance] = useState(null);

  const loadAllowance = useCallback(async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch('/api/sk-club/exchange-allowance', {
        headers: { authorization: process.env.NEXT_PUBLIC_API_KEY, 'x-firebase-token': idToken },
      });
      const data = await response.json();
      setAllowance(data.success ? data : null);
    } catch (error) {
      console.error('Exchange allowance error:', error);
      setAllowance(null);
    }
  }, [getIdToken]);

  useEffect(() => {
    loadAllowance();
  }, [loadAllowance]);

  const isTreatsToCoins = direction === 'treats_to_coins';
  const minAmount = isTreatsToCoins ? config.skClub.minTreatsExchange : config.skClub.minCoinsExchange;

  const numAmount = parseInt(amount) || 0;
  const preview = isTreatsToCoins
    ? Math.floor(numAmount / config.skClub.treatsPerCoin)
    : Math.floor(numAmount / config.skClub.coinsPerTreat);

  // Weekly caps are in coins (in) and TREATS (out); convert to the input's unit.
  const weeklyRemaining = !allowance
    ? 0
    : isTreatsToCoins
      ? allowance.coinsInRemaining * config.skClub.treatsPerCoin
      : allowance.treatsOutRemaining * config.skClub.coinsPerTreat;
  const balance = isTreatsToCoins ? walletBalance : Math.max(0, coinBalance + (allowance?.pendingCoinDelta || 0));
  const maxAmount = Math.min(balance, weeklyRemaining);
  const withinWeeklyLimit = numAmount <= weeklyRemaining;
  const canExchange = Boolean(allowance?.appReady);
  const hasEnough = numAmount <= balance;
  const meetsMinimum = numAmount >= minAmount;

  const handleExchange = async () => {
    if (!activeAddress || !numAmount || !meetsMinimum || !hasEnough || !withinWeeklyLimit || !canExchange) return;

    setExchanging(true);
    try {
      const idToken = await getIdToken();

      if (isTreatsToCoins) {
        const params = await algodClient.getTransactionParams().do();
        const txn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
          suggestedParams: params,
          from: activeAddress,
          to: process.env.NEXT_PUBLIC_SHITTY_UTILITY_ADDRESS,
          assetIndex: parseInt(process.env.NEXT_PUBLIC_TREAT_TOKEN_ID),
          amount: numAmount,
        });

        const encoded = algosdk.encodeUnsignedTransaction(txn);
        const signed = await signTransactions([encoded]);
        const { txId } = await algodClient.sendRawTransaction(signed[0]).do();
        await algosdk.waitForConfirmation(algodClient, txId, 10);

        const response = await fetch('/api/sk-club/exchange-treats-to-coins', {
          method: 'POST',
          headers: {
            authorization: process.env.NEXT_PUBLIC_API_KEY,
            'x-firebase-token': idToken,
          },
          body: JSON.stringify({ treatsAmount: numAmount, txnId: txId }),
        });

        const data = await response.json();
        if (data.success) {
          toast.success(`Exchanged ${numAmount.toLocaleString()} TREATS for ${data.coinsAdded.toLocaleString()} coins. They land next time you open Frik'd Up Fishies.`);
          setAmount('');
        } else {
          toast.error(data.error || 'Exchange failed');
        }
      } else {
        const response = await fetch('/api/sk-club/exchange-coins-to-treats', {
          method: 'POST',
          headers: {
            authorization: process.env.NEXT_PUBLIC_API_KEY,
            'x-firebase-token': idToken,
          },
          body: JSON.stringify({ coinAmount: numAmount }),
        });

        const data = await response.json();
        if (data.success) {
          toast.success(`Exchanged ${data.coinsSpent.toLocaleString()} coins for ${data.treatsSent.toLocaleString()} TREATS. Your coin balance updates next time you open Frik'd Up Fishies.`);
          setAmount('');
        } else {
          toast.error(data.error || 'Exchange failed');
        }
      }
    } catch (error) {
      console.error('Exchange error:', error);
      if (error.message?.includes('cancelled') || error.message?.includes('rejected')) {
        toast.error('Transaction cancelled');
      } else {
        toast.error('Exchange failed. Please try again.');
      }
    } finally {
      setExchanging(false);
      loadAllowance();
    }
  };

  if (!activeAddress) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className='relative group'
      >
        <div className='absolute -inset-[1px] bg-gradient-to-r from-secondary-500/20 via-transparent to-secondary-500/20 rounded-xl blur-[1px]' />
        <div className='relative bg-gray-800 rounded-xl p-6 border border-gray-700/50'>
          <div className='flex items-center gap-2 mb-3'>
            <div className='w-2 h-2 rounded-full bg-secondary-500' />
            <h3 className='text-sm font-bold text-gray-400 uppercase tracking-wider'>Exchange</h3>
          </div>
          <p className='text-gray-400'>Connect your wallet to exchange TREATS and coins.</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className='relative group'
    >
      <div className='absolute -inset-[1px] bg-gradient-to-r from-secondary-500/20 via-transparent to-secondary-500/20 rounded-xl blur-[1px] group-hover:blur-[2px] transition-all duration-300' />
      <div className='relative bg-gray-800 rounded-xl p-6 border border-gray-700/50'>
        <div className='flex items-center gap-2 mb-5'>
          <div className='w-2 h-2 rounded-full bg-secondary-500' />
          <h3 className='text-sm font-bold text-gray-400 uppercase tracking-wider'>Exchange</h3>
        </div>

        {config.skClub.coinsToTreatsEnabled && (
        <div className='flex rounded-lg overflow-hidden mb-5 border border-gray-700/50'>
          <button
            onClick={() => { setDirection('treats_to_coins'); setAmount(''); }}
            className={`flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-200 ${isTreatsToCoins ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20' : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700'}`}
          >
            TREATS &rarr; Coins
          </button>
          <button
            onClick={() => { setDirection('coins_to_treats'); setAmount(''); }}
            className={`flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-200 ${!isTreatsToCoins ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20' : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700'}`}
          >
            Coins &rarr; TREATS
          </button>
        </div>
        )}

        <div className='bg-gray-700/30 rounded-lg px-4 py-2.5 mb-5 border border-gray-700/50'>
          <p className='text-sm text-gray-400'>
            <span className='text-gray-500'>Rate:</span>{' '}
            <span className='text-white font-medium'>
              {isTreatsToCoins
                ? `${config.skClub.treatsPerCoin} TREATS = 1 coin`
                : `${config.skClub.coinsPerTreat} coins = 1 TREATS`
              }
            </span>
          </p>
        </div>

        <div className='mb-5'>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            {isTreatsToCoins ? 'TREATS to exchange' : 'Coins to exchange'}
          </label>
          <div className='flex gap-2'>
            <input
              type='number'
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Min ${minAmount}`}
              min={minAmount}
              max={maxAmount}
              className='flex-1 bg-gray-700/80 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-colors'
            />
            <button
              onClick={() => setAmount(String(maxAmount))}
              className='px-4 py-3 bg-gray-700/80 text-gray-400 rounded-lg text-sm font-medium hover:text-white hover:bg-gray-600 border border-gray-600 transition-all duration-200'
            >
              Max
            </button>
          </div>
          <p className='text-xs text-gray-500 mt-2'>
            Available: <span className='text-gray-400 font-medium'>{balance.toLocaleString()}</span> {isTreatsToCoins ? 'TREATS' : 'coins'}
          </p>
          {allowance && (
            <p className='text-xs text-gray-500 mt-1'>
              Weekly limit left:{' '}
              <span className='text-gray-400 font-medium'>
                {(isTreatsToCoins ? allowance.coinsInRemaining : allowance.treatsOutRemaining).toLocaleString()}
              </span>{' '}
              {isTreatsToCoins ? 'coins' : 'TREATS'}, resets {new Date(allowance.resetsAt).toLocaleDateString()}
            </p>
          )}
        </div>

        {numAmount > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className='bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-lg p-4 mb-5 border border-primary-500/20'
          >
            <p className='text-xs text-gray-400 uppercase tracking-wider font-medium'>You will receive</p>
            <p className='text-2xl font-bold text-white mt-1'>
              {preview.toLocaleString()} <span className='text-lg text-gray-400 font-normal'>{isTreatsToCoins ? 'coins' : 'TREATS'}</span>
            </p>
          </motion.div>
        )}

        {numAmount > 0 && !hasEnough && (
          <div className='flex items-center gap-2 text-red-400 text-sm mb-4 bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20'>
            <svg className='w-4 h-4 flex-shrink-0' fill='currentColor' viewBox='0 0 20 20'>
              <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
            </svg>
            Insufficient {isTreatsToCoins ? 'TREATS' : 'coins'}
          </div>
        )}
        {numAmount > 0 && hasEnough && !withinWeeklyLimit && (
          <div className='flex items-center gap-2 text-red-400 text-sm mb-4 bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20'>
            Over your weekly limit
          </div>
        )}
        {allowance && !canExchange && (
          <div className='flex items-center gap-2 text-red-400 text-sm mb-4 bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20'>
            {`Update Frik'd Up Fishies to ${config.skClub.minAppVersion} or later and open it once to use the exchange.`}
          </div>
        )}
        {numAmount > 0 && numAmount < minAmount && (
          <div className='flex items-center gap-2 text-red-400 text-sm mb-4 bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20'>
            <svg className='w-4 h-4 flex-shrink-0' fill='currentColor' viewBox='0 0 20 20'>
              <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
            </svg>
            Minimum: {minAmount} {isTreatsToCoins ? 'TREATS' : 'coins'}
          </div>
        )}

        <Button
          onClick={handleExchange}
          loading={exchanging}
          disabled={!numAmount || !meetsMinimum || !hasEnough || !withinWeeklyLimit || !canExchange || preview <= 0}
          className='w-full'
        >
          {isTreatsToCoins ? 'Exchange TREATS for Coins' : 'Exchange Coins for TREATS'}
        </Button>
      </div>
    </motion.div>
  );
}
